import { checkPan } from './pan';
import { GST_STATES } from './states';

/** The 36 characters GSTINs are written in; a character's index is its value in the check-digit sum. */
export const GSTIN_CHARSET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Shape of a GSTIN: state code, 10-character PAN (or TAN for some registrations), entity number,
 * a default character (usually "Z"), and the check character.
 */
const GSTIN_SHAPE = /^[0-9]{2}[0-9A-Z]{10}[1-9A-Z][0-9A-Z][0-9A-Z]$/;
const PAN_SHAPE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

/**
 * The check character for the first 14 characters of a GSTIN (Luhn mod 36):
 * odd positions (1st, 3rd…) weigh 1, even positions weigh 2; each product adds its quotient and
 * remainder by 36; the check character brings the total to a multiple of 36.
 */
export function gstinCheckChar(first14: string): string {
  if (first14.length !== 14) throw new Error('gstinCheckChar needs exactly 14 characters');
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const value = GSTIN_CHARSET.indexOf(first14.charAt(i));
    if (value < 0) throw new Error(`Not a GSTIN character: ${first14.charAt(i)}`);
    const product = value * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / 36) + (product % 36);
  }
  return GSTIN_CHARSET.charAt((36 - (sum % 36)) % 36);
}

/** Upper-case and drop the spaces and hyphens people paste in. */
export function normaliseGstin(raw: string): string {
  return String(raw ?? '')
    .toUpperCase()
    .replace(/[\s-]/g, '');
}

export type GstinProblem = 'empty' | 'too_short' | 'too_long' | 'format' | 'state' | 'check_digit';

export type GstinCheck =
  | { ok: true; gstin: string; stateCode: string; stateName: string; pan: string | null }
  | {
      ok: false;
      gstin: string;
      problem: GstinProblem;
      /** The likeliest intended GSTIN, when one character looks mistyped (the first of `suggestions`). */
      suggestion?: string;
      /** The valid GSTINs, at most three, that one small slip most likely turned into this one. */
      suggestions: string[];
    };

/** Just the offline rules: length, shape, state code and check character. */
function validate(
  gstin: string,
): { ok: true; stateCode: string; stateName: string } | { ok: false; problem: GstinProblem } {
  if (!gstin) return { ok: false, problem: 'empty' };
  if (gstin.length < 15) return { ok: false, problem: 'too_short' };
  if (gstin.length > 15) return { ok: false, problem: 'too_long' };
  if (!GSTIN_SHAPE.test(gstin)) return { ok: false, problem: 'format' };
  const stateCode = gstin.slice(0, 2);
  const stateName = GST_STATES[stateCode];
  if (!stateName) return { ok: false, problem: 'state' };
  if (gstinCheckChar(gstin.slice(0, 14)) !== gstin.charAt(14)) return { ok: false, problem: 'check_digit' };
  return { ok: true, stateCode, stateName };
}

/**
 * Check a GSTIN offline: length, shape, state code and check character. When it fails and one small
 * slip would explain it (a look-alike character such as O for 0, two characters swapped, a wrong last
 * character, one character typed twice), `suggestions` lists the valid GSTINs it could have been.
 */
export function checkGstin(raw: string): GstinCheck {
  const gstin = normaliseGstin(raw);
  const result = validate(gstin);
  if (!result.ok) {
    const suggestions = gstinSuggestions(gstin);
    return {
      ok: false,
      gstin,
      problem: result.problem,
      ...(suggestions[0] ? { suggestion: suggestions[0] } : {}),
      suggestions,
    };
  }
  const panPart = gstin.slice(2, 12);
  return {
    ok: true,
    gstin,
    stateCode: result.stateCode,
    stateName: result.stateName,
    pan: PAN_SHAPE.test(panPart) ? panPart : null,
  };
}

/** Characters people mix up when reading a GSTIN off a bill or typing it. */
const LOOK_ALIKES: Readonly<Record<string, string>> = {
  '0': 'OD',
  O: '0',
  D: '0',
  '1': 'IL',
  I: '1',
  L: '1',
  '2': 'Z',
  Z: '2',
  '5': 'S',
  S: '5',
  '6': 'G',
  G: '6',
  '8': 'B',
  B: '8',
};

/**
 * The valid GSTINs one small slip away from `gstin` (already normalised) that are likeliest, at most
 * three: a wrong last character, a look-alike (0 for O, 5 for S…), two neighbouring characters swapped,
 * or one character typed twice. A candidate that looks like a real registration is likelier: its PAN
 * part is a PAN (with a real holder letter, such as P or C), its 13th character is a digit (the
 * registration number for that PAN in the state) and its 14th is the usual "Z".
 *
 * Fixing the last character always gives a valid GSTIN, so it proves nothing on its own. It's listed
 * first, but never instead of an equally likely slip: a swap or look-alike that checks out is usually
 * the real mistake (by chance, only 1 in 36 would).
 */
export function gstinSuggestions(gstin: string): string[] {
  const found = new Map<string, number>();
  const consider = (candidate: string) => {
    if (candidate === gstin || found.has(candidate) || !validate(candidate).ok) return;
    const likely =
      (checkPan(candidate.slice(2, 12)).ok ? 4 : 0) +
      (/[1-9]/.test(candidate.charAt(12)) ? 1 : 0) +
      (candidate.charAt(13) === 'Z' ? 1 : 0);
    found.set(candidate, likely);
  };
  const known = [...gstin].every((c) => GSTIN_CHARSET.includes(c));
  if (gstin.length === 15 && known) {
    consider(gstin.slice(0, 14) + gstinCheckChar(gstin.slice(0, 14)));
    for (let i = 0; i < 15; i++) {
      for (const alt of LOOK_ALIKES[gstin.charAt(i)] ?? '') consider(gstin.slice(0, i) + alt + gstin.slice(i + 1));
      if (i < 14 && gstin.charAt(i) !== gstin.charAt(i + 1)) {
        consider(gstin.slice(0, i) + gstin.charAt(i + 1) + gstin.charAt(i) + gstin.slice(i + 2));
      }
    }
  } else if (gstin.length === 16 && known) {
    // One character typed twice (or one extra): try leaving each one out.
    for (let i = 0; i < 16; i++) consider(gstin.slice(0, i) + gstin.slice(i + 1));
  }
  // Offer only the likeliest: one clear answer, or the few that are equally likely.
  const best = Math.max(...found.values());
  return [...found.entries()]
    .filter(([, score]) => score === best)
    .slice(0, 3)
    .map(([candidate]) => candidate);
}

export function isValidGstin(raw: string): boolean {
  return checkGstin(raw).ok;
}

/** GSTIN shown in lists: first four and last three characters, e.g. "29AA…1Z0". */
export function maskGstin(gstin: string): string {
  const g = normaliseGstin(gstin);
  return g.length <= 7 ? g : `${g.slice(0, 4)}…${g.slice(-3)}`;
}
